import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import client from 'braintree-web/client';
import hostedFields from 'braintree-web/hosted-fields';
import paypalCheckout from 'braintree-web/paypal-checkout';
import googlePayment from 'braintree-web/google-payment';
import venmo from 'braintree-web/venmo';
import applePay from 'braintree-web/apple-pay';
import { customToast } from '@/utils';
import { coreDomain } from '@/redux/constant';
import { CheckIcon } from '@/assets/icons/CheckIcon';
import { CardFrontIcon } from '@/assets/icons/CardFrontIcon';
import { PayPalLogoIcon } from '@/assets/icons/PayPalLogoIcon';
import { VenmoLogoIcon } from '@/assets/icons/VenmoLogoIcon';
import { ApplePayLogoIcon } from '@/assets/icons/ApplePayLogoIcon';
import { GooglePayLogoIcon } from '@/assets/icons/GooglePayLogoIcon';
import { VisaCardIcon } from '@/assets/icons/VisaCardIcon';
import { MasterCardIcon } from '@/assets/icons/MasterCardIcon';
import { UnionPayCardIcon } from '@/assets/icons/UnionPayCardIcon';
import { AmexCardIcon } from '@/assets/icons/AmexCardIcon';
import { JcbCardIcon } from '@/assets/icons/JcbCardIcon';
import { DiscoverCardIcon } from '@/assets/icons/DiscoverCardIcon';

const DEFAULT_CARD_NETWORKS = ['visa', 'mastercard'];
const APPLE_PAY_SESSION_VERSION = 2;
const GOOGLE_PAY_SCRIPT_SRC = 'https://pay.google.com/gp/p/js/pay.js';
const SETUP_TIMEOUT_MS = 30000;
const VENMO_CANCEL_CODES = [
  'VENMO_APP_CANCELED',
  'VENMO_DESKTOP_CANCELED',
  'VENMO_CUSTOMER_CANCELED',
  'VENMO_CANCELED',
  'VENMO_TOKENIZATION_CANCELED_BY_MERCHANT',
  'FRAME_SERVICE_FRAME_CLOSED'
];
const VENMO_OPTIONS = {
  allowNewBrowserTab: true,
  allowDesktop: true,
  allowDesktopWebLogin: true,
  mobileWebFallBack: true,
  paymentMethodUsage: 'multi_use'
};

const PAYMENT_OPTIONS = [
  { id: 'card', label: 'Card', sheetLabel: 'Pay with card', Icon: CardFrontIcon },
  { id: 'paypal', label: 'PayPal', sheetLabel: 'PayPal', payloadType: 'PayPalAccount', Icon: PayPalLogoIcon },
  { id: 'venmo', label: 'Venmo', sheetLabel: 'Venmo', payloadType: 'VenmoAccount', Icon: VenmoLogoIcon },
  { id: 'applePay', label: 'Apple Pay', sheetLabel: 'Apple Pay', payloadType: 'ApplePayCard', Icon: ApplePayLogoIcon },
  { id: 'googlePay', label: 'Google Pay', sheetLabel: 'Google Pay', payloadType: 'AndroidPayCard', Icon: GooglePayLogoIcon }
];

const PaymentLogo = ({ option }) =>
  option ? (
    <option.Icon
      aria-hidden='true'
      className={`shrink-0 ${option.id === 'card' ? 'border border-[#B5B5B5] rounded-[3px]' : ''}`}
    />
  ) : null;

const CARD_FIELDS = ['number', 'expirationDate', 'cvv'];
const CARD_BRANDS = [
  { type: 'visa', configurationType: 'Visa', Icon: VisaCardIcon },
  { type: 'master-card', configurationType: 'MasterCard', Icon: MasterCardIcon },
  { type: 'unionpay', configurationType: 'UnionPay', Icon: UnionPayCardIcon },
  { type: 'american-express', configurationType: 'American Express', Icon: AmexCardIcon },
  { type: 'jcb', configurationType: 'JCB', Icon: JcbCardIcon },
  { type: 'discover', configurationType: 'Discover', Icon: DiscoverCardIcon }
];

const CardBrandLogo = ({ type, className = '' }) => {
  const brand = CARD_BRANDS.find(item => item.type === type);
  return brand ? <brand.Icon aria-hidden='true' className={`shrink-0 ${className}`} /> : null;
};

const CARD_FIELD_ERRORS = {
  number: { empty: 'Please fill out a card number.', invalid: 'This card number is not valid.' },
  expirationDate: { empty: 'Please fill out an expiration date.', invalid: 'This expiration date is not valid.' },
  cvv: { empty: 'Please fill out a CVV.', invalid: 'This security code is not valid.' }
};

const loadGooglePayScript = () =>
  window.google?.payments?.api
    ? Promise.resolve()
    : new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GOOGLE_PAY_SCRIPT_SRC;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load Google Pay'));
      document.head.appendChild(script);
    });

const withTimeout = (promise, name) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${name} setup timed out`)), SETUP_TIMEOUT_MS))
  ]);

const reportError = err => {
  console.error('[Braintree] error:', err);
  customToast.error(err?.message || 'Payment widget error. Please try again.');
};

const BillingModal = ({
  onPaymentMethodAvailable,
  onPaymentMethodNotAvailable,
  onBraintreeInstance,
  onPaymentPayload,
  onPaymentFieldInteraction,
  resetToken = 0,
  amount = '9.00',
  currency = 'USD',
  countryCode = 'US',
  merchantName = 'ILOLA',
  googlePayMerchantId = 'BCR2DN4T6PYZDGIQ',
  supportedCardNetworks = DEFAULT_CARD_NETWORKS
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [view, setView] = useState('options');
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [isCardValid, setIsCardValid] = useState(false);
  const [cardErrors, setCardErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [supportedCardTypes, setSupportedCardTypes] = useState([]);
  const [detectedCardType, setDetectedCardType] = useState(null);

  const numberRef = useRef(null);
  const expirationDateRef = useRef(null);
  const cvvRef = useRef(null);
  const paypalRef = useRef(null);
  const googlePayRef = useRef(null);
  const isMountedRef = useRef(true);
  const callbacksRef = useRef({});
  const actionsRef = useRef({});

  useEffect(() => {
    callbacksRef.current = {
      onPaymentMethodAvailable,
      onPaymentMethodNotAvailable,
      onBraintreeInstance,
      onPaymentPayload,
      onPaymentFieldInteraction
    };
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const instances = [];
    let cancelled = false;
    let currentView = 'options';
    let latestPayload = null;
    let cardFields = null;
    let isCardFormValid = false;
    let renderPayPal = null;
    let startVenmo = null;
    let startApplePay = null;

    const track = instance => {
      instances.push(instance);
      return instance;
    };

    const isActive = () => !cancelled && isMountedRef.current;
    const notifyInteraction = () => callbacksRef.current.onPaymentFieldInteraction?.();

    const setAvailable = isAvailable => {
      if (!isActive()) return;
      if (isAvailable) {
        callbacksRef.current.onPaymentMethodAvailable?.();
      } else {
        callbacksRef.current.onPaymentMethodNotAvailable?.();
      }
    };

    const selectPayload = payload => {
      if (!isActive() || !payload?.nonce) return;
      const option = PAYMENT_OPTIONS.find(item => item.payloadType === payload.type);
      latestPayload = payload;
      currentView = option?.id ?? currentView;
      setView(currentView);
      setSelectedPayload(payload);
      callbacksRef.current.onPaymentPayload?.(payload);
      setAvailable(true);
    };

    const setFieldError = (field, message) =>
      setCardErrors(prev => (prev[field] === message ? prev : { ...prev, [field]: message }));

    const openView = nextView => {
      if (!isActive()) return;
      notifyInteraction();
      currentView = nextView;
      setView(nextView);
      if (nextView === 'card') setTimeout(() => cardFields?.focus('number'), 50);
      setAvailable(nextView === 'card' && isCardFormValid);
    };

    const chooseAnotherWay = () => {
      if (!isActive()) return;
      latestPayload = null;
      setSelectedPayload(null);
      callbacksRef.current.onPaymentPayload?.(null);
      CARD_FIELDS.forEach(field => cardFields?.clear(field));
      setCardErrors({});
      setDetectedCardType(null);
      currentView = 'options';
      setView('options');
      setAvailable(false);
    };

    actionsRef.current = {
      openView,
      chooseAnotherWay,
      renderPayPal: () => renderPayPal?.(),
      startVenmo: () => startVenmo?.(),
      startApplePay: () => startApplePay?.()
    };

    const setupCard = async (clientInstance, gatewayConfiguration) => {
      setSupportedCardTypes(gatewayConfiguration.creditCards?.supportedCardTypes ?? []);
      cardFields = track(
        await hostedFields.create({
          client: clientInstance,
          fields: {
            number: { container: numberRef.current, placeholder: '•••• •••• •••• ••••' },
            expirationDate: { container: expirationDateRef.current, placeholder: 'MM/YY' },
            cvv: { container: cvvRef.current, placeholder: '•••' }
          },
          styles: { input: { 'font-size': '16px', color: '#000' } }
        })
      );

      cardFields.on('focus', event => {
        setFocusedField(event.emittedBy);
        notifyInteraction();
      });

      cardFields.on('blur', event => {
        const field = event.fields[event.emittedBy];
        setFocusedField(null);
        if (field.isEmpty) {
          setFieldError(event.emittedBy, CARD_FIELD_ERRORS[event.emittedBy].empty);
        } else if (!field.isValid) {
          setFieldError(event.emittedBy, CARD_FIELD_ERRORS[event.emittedBy].invalid);
        }
      });

      cardFields.on('cardTypeChange', event => {
        const card = event.cards?.length === 1 ? event.cards[0] : null;
        setDetectedCardType(card?.type ?? null);
        cardFields
          .setAttribute({ field: 'cvv', attribute: 'placeholder', value: '•'.repeat(card?.code?.size ?? 3) })
          .catch(() => {});
      });

      cardFields.on('validityChange', event => {
        const field = event.fields[event.emittedBy];
        if (field.isValid) {
          setFieldError(event.emittedBy, null);
        } else if (!field.isPotentiallyValid) {
          setFieldError(event.emittedBy, CARD_FIELD_ERRORS[event.emittedBy].invalid);
        }

        const isValid = Object.values(event.fields).every(item => item.isValid);
        if (isValid === isCardFormValid) return;
        isCardFormValid = isValid;
        setIsCardValid(isValid);
        if (currentView === 'card') setAvailable(isValid);
      });
    };

    const setupPayPal = async (clientInstance, gatewayConfiguration) => {
      if (!gatewayConfiguration.paypalEnabled) return false;

      const paypalInstance = track(await paypalCheckout.create({ client: clientInstance }));
      if (!window.paypal?.Buttons) {
        await paypalInstance.loadPayPalSDK({ vault: true });
      }

      const buttons = window.paypal.Buttons({
        fundingSource: window.paypal.FUNDING.PAYPAL,
        onClick: notifyInteraction,
        createBillingAgreement: () => paypalInstance.createPayment({ flow: 'vault', amount, currency }),
        onApprove: data => paypalInstance.tokenizePayment(data).then(selectPayload),
        onError: reportError
      });
      if (!buttons.isEligible()) return false;

      track({ teardown: () => buttons.close() });
      let isRendered = false;
      renderPayPal = () => {
        if (isRendered) return;
        isRendered = true;
        buttons.render(paypalRef.current).catch(err => {
          isRendered = false;
          reportError(err);
        });
      };
      return true;
    };

    const setupGooglePay = async (clientInstance, gatewayConfiguration) => {
      if (!gatewayConfiguration.androidPay) return false;

      await loadGooglePayScript();
      const googlePayInstance = track(
        await googlePayment.create({ client: clientInstance, googlePayVersion: 2, googleMerchantId: googlePayMerchantId })
      );
      const paymentsClient = new window.google.payments.api.PaymentsClient({
        environment: gatewayConfiguration.environment === 'production' ? 'PRODUCTION' : 'TEST'
      });
      const createRequest = () =>
        googlePayInstance.createPaymentDataRequest({
          merchantInfo: { merchantName, merchantId: googlePayMerchantId },
          transactionInfo: { totalPriceStatus: 'FINAL', totalPrice: amount, currencyCode: currency },
          allowedPaymentMethods: [
            { type: 'CARD', parameters: { billingAddressRequired: true, billingAddressParameters: { format: 'FULL' } } }
          ]
        });

      const { result } = await paymentsClient.isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: createRequest().allowedPaymentMethods
      });
      if (!result) return false;

      const button = paymentsClient.createButton({
        buttonType: 'plain',
        buttonSizeMode: 'fill',
        onClick: () => {
          notifyInteraction();
          paymentsClient
            .loadPaymentData(createRequest())
            .then(paymentData =>
              googlePayInstance
                .parseResponse(paymentData)
                .then(payload => selectPayload({ ...payload, details: { ...payload.details, rawPaymentData: paymentData } }))
            )
            .catch(err => {
              if (err?.statusCode !== 'CANCELED') reportError(err);
            });
        }
      });
      googlePayRef.current?.replaceChildren(button);
      return true;
    };

    const setupVenmo = async (clientInstance, gatewayConfiguration) => {
      if (!gatewayConfiguration.payWithVenmo || !venmo.isBrowserSupported(VENMO_OPTIONS)) return false;

      const venmoInstance = track(await venmo.create({ client: clientInstance, ...VENMO_OPTIONS }));
      const tokenize = () =>
        venmoInstance
          .tokenize()
          .then(selectPayload)
          .catch(err => {
            if (!VENMO_CANCEL_CODES.includes(err?.code)) reportError(err);
          });

      if (venmoInstance.hasTokenizationResult()) tokenize();

      startVenmo = () => {
        notifyInteraction();
        tokenize();
      };
      return true;
    };

    const setupApplePay = async (clientInstance, gatewayConfiguration) => {
      const ApplePaySession = window.ApplePaySession;
      if (!gatewayConfiguration.applePayWeb || !ApplePaySession || window.location.protocol !== 'https:') return false;
      if (!ApplePaySession.supportsVersion(APPLE_PAY_SESSION_VERSION) || !ApplePaySession.canMakePayments()) return false;

      const applePayInstance = track(await applePay.create({ client: clientInstance }));
      let isSessionInProgress = false;

      startApplePay = () => {
        if (isSessionInProgress) return;
        isSessionInProgress = true;
        notifyInteraction();

        const session = new ApplePaySession(
          APPLE_PAY_SESSION_VERSION,
          applePayInstance.createPaymentRequest({
            total: { label: merchantName, amount },
            countryCode,
            currencyCode: currency,
            supportedNetworks: supportedCardNetworks
          })
        );

        session.onvalidatemerchant = event => {
          applePayInstance
            .performValidation({ validationURL: event.validationURL, displayName: merchantName })
            .then(merchantSession => session.completeMerchantValidation(merchantSession))
            .catch(err => {
              isSessionInProgress = false;
              session.abort();
              reportError(err);
            });
        };

        session.onpaymentauthorized = event => {
          applePayInstance
            .tokenize({ token: event.payment.token })
            .then(payload => {
              session.completePayment(ApplePaySession.STATUS_SUCCESS);
              selectPayload({ ...payload, details: { ...payload.details, rawPaymentData: event.payment } });
            })
            .catch(err => {
              session.completePayment(ApplePaySession.STATUS_FAILURE);
              reportError(err);
            })
            .finally(() => {
              isSessionInProgress = false;
            });
        };

        session.oncancel = () => {
          isSessionInProgress = false;
        };

        session.begin();
      };
      return true;
    };

    const setupOption = (name, setup) =>
      withTimeout(setup(), name).catch(err => {
        console.warn(`[Braintree] ${name} unavailable:`, err);
        return false;
      });

    const init = async () => {
      try {
        setIsLoading(true);
        setView('options');
        setSelectedPayload(null);
        setIsCardValid(false);
        setCardErrors({});
        setDetectedCardType(null);
        setAvailableOptions([]);

        const response = await fetch(`${coreDomain}/ChargeBeePayments/GetClientToken`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Failed to get client token: ${response.status}`);
        const data = await response.json();
        if (!data?.result) throw new Error('Invalid client token response');
        if (!isActive()) return;

        const clientInstance = track(await client.create({ authorization: data.result }));
        const gatewayConfiguration = clientInstance.getConfiguration().gatewayConfiguration;

        const [, hasPayPal, hasVenmo, hasApplePay, hasGooglePay] = await Promise.all([
          setupCard(clientInstance, gatewayConfiguration),
          setupOption('PayPal', () => setupPayPal(clientInstance, gatewayConfiguration)),
          setupOption('Venmo', () => setupVenmo(clientInstance, gatewayConfiguration)),
          setupOption('Apple Pay', () => setupApplePay(clientInstance, gatewayConfiguration)),
          setupOption('Google Pay', () => setupGooglePay(clientInstance, gatewayConfiguration))
        ]);
        if (!isActive()) return;

        const available = {
          card: true,
          paypal: hasPayPal,
          venmo: hasVenmo,
          applePay: hasApplePay,
          googlePay: hasGooglePay
        };
        setAvailableOptions(PAYMENT_OPTIONS.filter(option => available[option.id]).map(option => option.id));

        callbacksRef.current.onBraintreeInstance?.({
          requestPaymentMethod: callback => {
            const request = latestPayload ? Promise.resolve(latestPayload) : cardFields.tokenize();
            request.then(payload => callback(null, payload), err => callback(err));
          }
        });

        setError(null);
        setIsLoading(false);
      } catch (err) {
        if (err?.name === 'AbortError' || !isActive()) return;
        console.error('Error initializing Braintree:', err);
        setError('Failed to initialize the payment form. Please try again.');
        setIsLoading(false);
      }
    };

    init();

    const googlePayContainer = googlePayRef.current;

    return () => {
      cancelled = true;
      controller.abort();
      actionsRef.current = {};
      googlePayContainer?.replaceChildren();
      instances.reverse().forEach(instance =>
        Promise.resolve()
          .then(() => instance.teardown())
          .catch(err => console.warn('Braintree teardown failed:', err))
      );
    };
  }, [retryKey, resetToken, amount, currency, countryCode, merchantName, googlePayMerchantId, supportedCardNetworks]);

  useEffect(() => {
    if (view === 'paypal') actionsRef.current.renderPayPal?.();
  }, [view]);

  const handleRetry = () => {
    setError(null);
    setRetryKey(k => k + 1);
  };

  const activeOption = PAYMENT_OPTIONS.find(option => option.id === view);
  const selectedDetails = selectedPayload?.details ?? {};
  const selectedDescription =
    selectedDetails.email ||
    selectedDetails.username ||
    [selectedDetails.cardType, selectedDetails.lastFour && `ending in ${selectedDetails.lastFour}`].filter(Boolean).join(' ');

  const labelClassName = 'mb-1 text-sm text-black font-outfit';
  const getFieldClassName = field =>
    `h-[50px] px-3 bg-white border ${cardErrors[field] ? 'border-secondary-rustyRed' : 'border-black'} ${focusedField === field ? 'ring-1 ring-black' : ''}`;

  const acceptedCardBrands = CARD_BRANDS.filter(brand => supportedCardTypes.includes(brand.configurationType));
  const hasDetectedBrandLogo = CARD_BRANDS.some(brand => brand.type === detectedCardType);

  const renderCardField = (field, label, ref, { hint, icon } = {}) => (
    <div>
      <p className={labelClassName}>
        {label}
        {hint ? <span className='ml-2 text-xs text-neutral'>{hint}</span> : null}
      </p>
      <div className='relative'>
        <div ref={ref} className={`${getFieldClassName(field)} ${icon ? 'pr-16' : ''}`} />
        {icon ? (
          <CardBrandLogo type={icon} className='absolute -translate-y-1/2 border border-[#B5B5B5] rounded-[3px] right-3 top-1/2' />
        ) : null}
      </div>
      {cardErrors[field] ? <p className='mt-1 text-xs text-secondary-rustyRed font-outfit'>{cardErrors[field]}</p> : null}
    </div>
  );

  return (
    <Box>
      {error && (
        <Box role='alert' sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
          <Typography variant='body2' color='error'>
            {error}
          </Typography>
          <Button onClick={handleRetry} variant='outlined' size='small' sx={{ alignSelf: 'flex-start' }}>
            Retry
          </Button>
        </Box>
      )}

      {!error && (
        <Box sx={{ position: 'relative', width: '100%', minHeight: 240 }}>
          {isLoading && (
            <Box
              role='status'
              aria-live='polite'
              aria-busy
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 3,
                borderRadius: 1,
                bgcolor: theme => theme.palette.action.hover
              }}
            >
              <CircularProgress size={22} thickness={2} />
              <Typography component='h3' variant='subtitle1' fontWeight={600} textAlign='center' gutterBottom>
                Loading secure payment
              </Typography>
              <Typography variant='body2' color='text.secondary' textAlign='center' sx={{ maxWidth: 320, mb: 2 }}>
                Please wait while we connect to the payment provider. This may take a few seconds on a slow
                connection.
              </Typography>
              <Box sx={{ width: '100%', maxWidth: 360 }}>
                <Skeleton variant='rounded' height={10} sx={{ mb: 1.25 }} />
                <Skeleton variant='rounded' height={10} width='85%' sx={{ mb: 1.25 }} />
                <Skeleton variant='rounded' height={44} />
              </Box>
            </Box>
          )}

          <div className={`space-y-4 transition-opacity duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
            <div className={view === 'options' ? '' : 'hidden'}>
              <p className='mb-3 text-base text-black font-outfit'>Choose a way to pay</p>
              <div className='overflow-hidden border border-[#B5B5B5] rounded'>
                {PAYMENT_OPTIONS.filter(option => availableOptions.includes(option.id)).map(option => (
                  <button
                    key={option.id}
                    type='button'
                    onClick={() => actionsRef.current.openView?.(option.id)}
                    className='flex items-center w-full gap-4 px-4 py-3 text-left text-black border-b border-[#B5B5B5] last:border-b-0 bg-[#FAFAFA] hover:bg-white hover:font-medium font-outfit'
                  >
                    <PaymentLogo option={option} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {selectedPayload ? (
              <div className='space-y-4'>
                <p className='text-base text-black font-outfit'>Paying with {activeOption?.label}</p>
                <div className='flex items-center gap-4 px-4 py-3 border-2 border-[#219653] rounded-[5px] bg-white'>
                  <PaymentLogo option={activeOption} />
                  <div className='flex-1 min-w-0'>
                    <p className='font-semibold text-black font-outfit'>{activeOption?.label}</p>
                    {selectedDescription ? (
                      <p className='text-sm truncate text-neutral font-outfit'>{selectedDescription}</p>
                    ) : null}
                  </div>
                  <span className='flex items-center justify-center rounded-full size-10 shrink-0 bg-[#219653]'>
                    <CheckIcon width={28} height={28} />
                  </span>
                </div>
              </div>
            ) : null}

            <div className={view === 'options' || selectedPayload ? 'hidden' : 'border border-[#B5B5B5] rounded'}>
              <div className='px-4 py-3 border-b border-[#B5B5B5]'>
                <div className='flex items-center gap-4'>
                  <PaymentLogo option={activeOption} />
                  <p className='text-black font-outfit'>{activeOption?.sheetLabel}</p>
                </div>
                {view === 'card' && acceptedCardBrands.length ? (
                  <div className='flex flex-wrap gap-2 mt-3'>
                    {acceptedCardBrands.map(brand => (
                      <CardBrandLogo key={brand.type} type={brand.type} />
                    ))}
                  </div>
                ) : null}
              </div>
              <div className='p-4'>
                <div className={view === 'card' ? 'space-y-3' : 'hidden'}>
                  {renderCardField('number', 'Card Number', numberRef, {
                    icon: hasDetectedBrandLogo ? detectedCardType : null
                  })}
                  {renderCardField('expirationDate', 'Expiration Date', expirationDateRef, { hint: '(MM/YY)' })}
                  {renderCardField('cvv', 'CVV', cvvRef, {
                    hint: detectedCardType === 'american-express' ? '(4 digits)' : '(3 digits)'
                  })}
                  {isCardValid ? <p className='text-sm text-[#219653] font-outfit'>Card details look good ✓</p> : null}
                  <a
                    href='https://www.paypal.com/us/legalhub/home'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='block text-xs text-black font-outfit hover:underline'
                  >
                    By paying with my card, I agree to the PayPal Privacy Statement.
                  </a>
                </div>
                <div ref={paypalRef} className={view === 'paypal' ? '' : 'hidden'} />
                <div ref={googlePayRef} className={view === 'googlePay' ? 'h-11' : 'hidden'} />
                {view === 'venmo' ? (
                  <button
                    type='button'
                    onClick={() => actionsRef.current.startVenmo?.()}
                    className='w-full h-11 rounded bg-[#008CFF] text-white font-outfit font-semibold'
                  >
                    Venmo
                  </button>
                ) : null}
                {view === 'applePay' ? (
                  <button
                    type='button'
                    aria-label='Pay with Apple Pay'
                    onClick={() => actionsRef.current.startApplePay?.()}
                    className='block w-full h-11 rounded [-webkit-appearance:-apple-pay-button] [-apple-pay-button-type:plain] [-apple-pay-button-style:black]'
                  />
                ) : null}
              </div>
            </div>

            {view !== 'options' ? (
              <button
                type='button'
                onClick={() => actionsRef.current.chooseAnotherWay?.()}
                className='w-full py-4 text-sm text-black rounded bg-[#FAFAFA] hover:bg-[#FCFCFC] font-outfit'
              >
                <span className='border-b border-[#B5B5B5] pb-px'>Choose another way to pay</span>
              </button>
            ) : null}
          </div>
        </Box>
      )}
    </Box>
  );
};

export default BillingModal;
